import type { Msg } from "../../types/chat";
import { safeUuid } from "./chatUtils";

/* -- Fast boot snapshot cache ---------------------------------------- */
export const SNAPSHOT_KEY = '__echo_channel_snapshots__';
export const SNAPSHOT_MAX_AGE_MS = 72 * 60 * 60 * 1000; // 3 days
const SNAPSHOT_MAX_BYTES = 200_000; // keep storage small to avoid quota issues
export const SNAPSHOT_MSG_LIMIT = 30;

export type SnapshotMsg = Pick<Msg, 'id' | 'content' | 'createdAt' | 'updatedAt' | 'authorId' | 'authorName' | 'authorColor' | 'kind' | 'metadata' | 'reactions' | 'attachments' | 'spoiler' | 'seenBy' | 'seenByIds' | 'replyTo'>;
export type SnapshotPayload = {
  ts: number;
  channelMeta: { id: string; name?: string; type?: string }[];
  currentChannelId?: string;
  messages?: Record<string, SnapshotMsg[]>;
};

export function slimMessages(msgs: Msg[]): SnapshotMsg[] {
  return msgs
    .slice(-SNAPSHOT_MSG_LIMIT)
    .map((m) => ({
      id: m.id,
      content: m.content,
      createdAt: m.createdAt,
      updatedAt: m.updatedAt,
      authorId: m.authorId,
      authorName: m.authorName,
      authorColor: m.authorColor,
      kind: m.kind,
      metadata: m.metadata,
      reactions: m.reactions,
      attachments: m.attachments,
      spoiler: m.spoiler,
      seenBy: m.seenBy,
      seenByIds: m.seenByIds,
      replyTo: m.replyTo?.id ? m.replyTo : undefined,
    }));
}

export function loadSnapshot(spaceId: string): SnapshotPayload | null {
  if (!spaceId) return null;
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const snap = parsed?.[spaceId] as SnapshotPayload | undefined;
    if (!snap || typeof snap.ts !== 'number') return null;
    if (Date.now() - snap.ts > SNAPSHOT_MAX_AGE_MS) return null;
    return snap;
  } catch {
    return null;
  }
}

export function hydrateSnapshotMessages(raw?: Record<string, SnapshotMsg[]>): Record<string, Msg[]> {
  if (!raw) return {};
  const next: Record<string, Msg[]> = {};
  for (const [key, list] of Object.entries(raw)) {
    if (!Array.isArray(list)) continue;
    next[key] = list.slice(-SNAPSHOT_MSG_LIMIT).map((m) => ({
      id: m.id || safeUuid(),
      content: m.content || '',
      createdAt: m.createdAt,
      updatedAt: m.updatedAt ?? null,
      authorId: m.authorId,
      authorName: m.authorName,
      authorColor: m.authorColor ?? null,
      kind: m.kind,
      metadata: m.metadata,
      reactions: m.reactions || {},
      attachments: Array.isArray(m.attachments) ? m.attachments : undefined,
      spoiler: !!m.spoiler,
      seenBy: Array.isArray(m.seenBy) ? m.seenBy : undefined,
      seenByIds: Array.isArray(m.seenByIds) ? m.seenByIds : undefined,
      replyTo: m.replyTo?.id ? m.replyTo : undefined,
    }));
  }
  return next;
}

export function persistSnapshot(spaceId: string, channels: any[], currentChannelId: string, msgsByKey: Record<string, Msg[]>) {
  if (!spaceId || !channels?.length) return;
  try {
    const payload: SnapshotPayload = {
      ts: Date.now(),
      currentChannelId,
      channelMeta: channels.map((c) => ({ id: c.id, name: c.name, type: c.type || 'text' })),
      messages: {},
    };
    const prefix = `${spaceId}:`;
    for (const [key, msgs] of Object.entries(msgsByKey || {})) {
      if (!key.startsWith(prefix)) continue;
      if (!Array.isArray(msgs) || msgs.length === 0) continue;
      payload.messages![key] = slimMessages(msgs);
    }
    const existingRaw = localStorage.getItem(SNAPSHOT_KEY);
    const all = existingRaw ? JSON.parse(existingRaw) : {};
    all[spaceId] = payload;
    const serialized = JSON.stringify(all);
    if (serialized.length > SNAPSHOT_MAX_BYTES) return; // avoid blowing storage
    localStorage.setItem(SNAPSHOT_KEY, serialized);
  } catch {
    /* noop */
  }
}
